import {
  Info,
  Network,
  Plus,
  Radio,
  Server,
  Share2,
  Shield,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { CreateAtProviderForm } from '@/components/fleet/create-at-provider-form';
import { FleetTaskPanel } from '@/components/fleet/fleet-task-panel';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateServer, type CreateServerBody } from '@/hooks/use-create-server';
import { useFleetTask } from '@/hooks/use-fleet-task';
import { useResolveGeo } from '@/hooks/use-resolve-geo';
import { ApiError } from '@/lib/api-client';

/** Roles an operator can add from the dashboard — the VPN-fleet node types
 *  plus the v2ray_edge (VLESS-Reality stealth) edge. Infrastructure roles
 *  (gateway/management/db/…) are managed by the infra inventory, not here. */
const ROLES = ['vpn_server', 'exit_node', 'proxy_ingress', 'v2ray_edge', 'bootstrap_edge'] as const;

type Role = (typeof ROLES)[number];

/** Presentation metadata for the headline role picker. Order matches ROLES so
 *  the cards render in the same sequence the API/tests expect. */
const ROLE_META: Record<Role, { label: string; icon: LucideIcon; blurb: string }> = {
  vpn_server: {
    label: 'VPN server',
    icon: Server,
    blurb: 'WireGuard / AmneziaWG tunnel endpoint for clients.',
  },
  exit_node: {
    label: 'Exit node',
    icon: Network,
    blurb: 'Egress hop that routes user traffic out to the internet.',
  },
  proxy_ingress: {
    label: 'Proxy ingress',
    icon: Share2,
    blurb: 'Front-door ingress that fans traffic into the fleet.',
  },
  v2ray_edge: {
    label: 'Reality edge',
    icon: Shield,
    blurb: 'Reality stealth edge (VLESS) for censored regions.',
  },
  bootstrap_edge: {
    label: 'Bootstrap edge',
    icon: Radio,
    blurb: 'VLESS-Reality node routing to the API origin for censored-region bootstrap.',
  },
};

/**
 * Curated provider list — matches the providers actually used in the cost
 * spec + Ansible roles. "Other" routes the operator to free-text. Keeps
 * `vps_servers.provider` rows consistent (filter / KPI strip uses provider
 * as a grouping key — typos like "Hetzner" vs "hetzner" fragment the data).
 */
const PROVIDERS = [
  'hetzner',
  'ovh',
  'vultr',
  'buyvm',
  'privex',
  'aws',
  'azure',
  'gcp',
  'other',
] as const;

const EMPTY = {
  name: '',
  role: 'vpn_server' as string,
  public_ip: '',
  private_ip: '',
  provider: '',
  region_continent: '',
  region_country: '',
  region_city: '',
  vm_type: '',
};

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-border bg-card px-3 py-1 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40';

/**
 * "Add server" — a dialog over POST /servers.
 *
 * Required fields: name, role, public IP, provider. Empty optional fields are
 * dropped from the body so the API treats them as absent. On success the
 * operator is taken to the new server's detail page.
 */
interface AddServerDialogProps {
  /** When provided the dialog is controlled externally; no trigger button renders. */
  open?: boolean;
  onClose?: () => void;
}

export function AddServerDialog({ open: externalOpen, onClose }: AddServerDialogProps = {}): JSX.Element {
  const controlled = externalOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ? externalOpen : internalOpen;
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  // "Register existing" (the form below) vs. "Create at provider" (dispatches
  // a Semaphore Terraform run via CreateAtProviderForm — SPEC-0019).
  const [mode, setMode] = useState<'register' | 'create'>('register');
  // Set once CreateAtProviderForm dispatches a create — the dialog then shows
  // the task's live status instead of the form.
  const [dispatched, setDispatched] = useState<{ taskId: number; name: string } | null>(null);
  const dispatchedTask = useFleetTask(dispatched?.taskId);

  // Switching mode always drops a prior dispatch's status panel — re-entering
  // "Create at provider" starts a fresh form, it never resurfaces a stale task.
  function switchMode(next: 'register' | 'create'): void {
    setMode(next);
    setDispatched(null);
  }

  const setOpen = controlled
    ? (v: boolean) => {
        if (!v) {
          // Pre-existing register-mode behaviour, unchanged: form/error reset
          // on close. `mode`/`dispatched` are new state this dialog didn't
          // have before — only they get an explicit reset here.
          setForm({ ...EMPTY });
          setError(null);
          setMode('register');
          setDispatched(null);
          if (onClose) onClose();
        }
      }
    : (v: boolean) => {
        // Pre-existing register-mode behaviour, unchanged: form/error survive
        // an uncontrolled close (Esc / overlay click) — only reset explicitly
        // on submit. `mode`/`dispatched` still reset so reopening doesn't
        // resurface a stale create-at-provider panel.
        if (!v) {
          setMode('register');
          setDispatched(null);
        }
        setInternalOpen(v);
      };
  // Tracks which region fields we auto-filled vs. the operator typed. We
  // never overwrite a field they've touched — even if the IP resolution
  // came back later.
  const [geoFilled, setGeoFilled] = useState({ continent: false, country: false, city: false });
  const create = useCreateServer();
  const navigate = useNavigate();

  // Auto-resolve the region from public_ip via /admin/fleet/geo. Fires
  // once the IP looks well-formed (the hook's `enabled` guard handles the
  // partial-input case). Result is staleTime-cached so re-opening the
  // dialog for the same IP doesn't re-fetch.
  const geo = useResolveGeo(form.public_ip);

  useEffect(() => {
    if (!geo.data) return;
    const { continent, country, city } = geo.data;
    setForm((current) => {
      const next = { ...current };
      // Only fill fields the operator hasn't touched. If they typed
      // something, leave it; if they cleared a previously-auto-filled
      // value, also leave it (geoFilled flag tracks origin).
      if (continent && (!current.region_continent || geoFilled.continent)) {
        next.region_continent = continent;
      }
      if (country && (!current.region_country || geoFilled.country)) {
        next.region_country = country;
      }
      if (city && (!current.region_city || geoFilled.city)) {
        next.region_city = city;
      }
      return next;
    });
    setGeoFilled({
      continent: !!continent,
      country: !!country,
      city: !!city,
    });
  }, [geo.data, geoFilled.continent, geoFilled.country, geoFilled.city]);

  function set(field: keyof typeof EMPTY, value: string): void {
    setForm((current) => ({ ...current, [field]: value }));
    // Any manual edit to a region field clears its auto-filled flag so
    // the next geo result doesn't overwrite the operator's typed value.
    if (field === 'region_continent') setGeoFilled((g) => ({ ...g, continent: false }));
    if (field === 'region_country') setGeoFilled((g) => ({ ...g, country: false }));
    if (field === 'region_city') setGeoFilled((g) => ({ ...g, city: false }));
    setError(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);

    const body: CreateServerBody = {
      name: form.name.trim(),
      role: form.role,
      public_ip: form.public_ip.trim(),
      provider: form.provider.trim(),
    };
    if (form.private_ip.trim()) body.private_ip = form.private_ip.trim();
    if (form.region_continent.trim()) body.region_continent = form.region_continent.trim();
    if (form.region_country.trim()) body.region_country = form.region_country.trim();
    if (form.region_city.trim()) body.region_city = form.region_city.trim();
    if (form.vm_type.trim()) body.vm_type = form.vm_type.trim();

    create.mutate(body, {
      onSuccess: (result) => {
        toast.success(
          `Server ${result.server.name} ${result.outcome === 'created' ? 'created' : 'saved'}.`,
        );
        setOpen(false);
        setForm({ ...EMPTY });
        navigate(`/fleet/${encodeURIComponent(result.server.name)}`);
      },
      onError: (err) => {
        setError(err instanceof ApiError ? err.message : 'Could not create the server.');
      },
    });
  }

  return (
    <>
      {!controlled && (
        <Button size="sm" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Add server
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add a server</DialogTitle>
          </DialogHeader>

          <div className="mb-4 flex gap-2" role="group" aria-label="How to add the server">
            <Button
              type="button"
              variant={mode === 'register' ? 'default' : 'outline'}
              aria-pressed={mode === 'register'}
              onClick={() => switchMode('register')}
            >
              Register existing
            </Button>
            <Button
              type="button"
              variant={mode === 'create' ? 'default' : 'outline'}
              aria-pressed={mode === 'create'}
              onClick={() => switchMode('create')}
            >
              Create at provider
            </Button>
          </div>

          {mode === 'create' ? (
            dispatched ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Creating <strong className="text-foreground">{dispatched.name}</strong> at UpCloud —
                  the server appears in the fleet list once the VM answers on SSH.
                </p>
                <FleetTaskPanel taskId={dispatched.taskId} />
                <div className="flex justify-end gap-2 pt-1">
                  {dispatchedTask.data?.status === 'success' && (
                    <Button
                      onClick={() => {
                        const name = dispatched.name;
                        setOpen(false);
                        navigate(`/fleet/${encodeURIComponent(name)}`);
                      }}
                    >
                      Go to server
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <CreateAtProviderForm onDispatched={(taskId, name) => setDispatched({ taskId, name })} />
            )
          ) : (
            <>
              {/* For full provisioning use the Provision wizard; this dialog
                  only registers an already-set-up server in the registry. */}
              <div className="mb-1 flex items-start gap-2 rounded-md border border-border bg-secondary p-2.5 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                <p>
                  Registers an <em>already-provisioned</em> server of any role. To set up a fresh{' '}
                  <strong className="text-foreground">VPN server</strong> end-to-end (SSH + preflight +
                  provisioning), use the <strong className="text-foreground">Provision VPN</strong>{' '}
                  wizard instead.
                </p>
              </div>

              {/* autoComplete=off — this is server config, not credentials;
                  stops the browser password manager offering to fill/save. */}
              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                {/* Headline choice — pick the role first; it drives the help text
                    below. v2ray_edge (Reality) is a first-class card here, not
                    buried in a dropdown. */}
                <div className="space-y-2">
                  <Label>Role</Label>
                  <div
                    role="radiogroup"
                    aria-label="Server role"
                    className="grid grid-cols-2 gap-2"
                  >
                    {ROLES.map((r) => {
                      const meta = ROLE_META[r];
                      const Icon = meta.icon;
                      const selected = form.role === r;
                      return (
                        <button
                          key={r}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => set('role', r)}
                          className={[
                            'flex items-start gap-3 rounded-md border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40',
                            selected
                              ? 'border-primary bg-primary/10 text-foreground ring-1 ring-primary/30'
                              : 'border-border bg-card hover:border-primary/40 hover:bg-accent',
                          ].join(' ')}
                        >
                          <Icon
                            className={[
                              'mt-0.5 h-5 w-5 shrink-0',
                              selected ? 'text-primary' : 'text-muted-foreground',
                            ].join(' ')}
                            aria-hidden
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-medium">{meta.label}</span>
                            <span className="mt-0.5 block font-mono text-[10px] text-muted-foreground">
                              {r}
                            </span>
                            <span className="mt-1 block text-xs text-muted-foreground">
                              {meta.blurb}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="srv-name">Name</Label>
                  <Input
                    id="srv-name"
                    required
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder={form.role === 'v2ray_edge' ? 'reality-tehran-01' : 'exit-fra-01'}
                    className="font-mono"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="srv-public-ip" className="flex items-center gap-1">
                      Public IP
                      {geo.isFetching && (
                        <span className="text-[10px] text-muted-foreground">resolving…</span>
                      )}
                    </Label>
                    <Input
                      id="srv-public-ip"
                      required
                      value={form.public_ip}
                      onChange={(e) => set('public_ip', e.target.value)}
                      placeholder="203.0.113.10"
                      className="font-mono"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      {form.role === 'v2ray_edge'
                        ? 'The public IP clients dial for the Reality handshake. Region auto-fills from it.'
                        : 'Region fields auto-fill from this IP.'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="srv-private-ip">Private IP</Label>
                    <Input
                      id="srv-private-ip"
                      value={form.private_ip}
                      onChange={(e) => set('private_ip', e.target.value)}
                      placeholder="optional"
                      className="font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="srv-provider">Provider</Label>
                    <select
                      id="srv-provider"
                      required
                      className={SELECT_CLASS}
                      value={form.provider}
                      onChange={(e) => set('provider', e.target.value)}
                    >
                      <option value="">— select —</option>
                      {PROVIDERS.map((p) => (
                        <option key={p} value={p}>
                          {p === 'other' ? 'Other (specify in notes)' : p}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="srv-vm-type">VM type</Label>
                    <Input
                      id="srv-vm-type"
                      value={form.vm_type}
                      onChange={(e) => set('vm_type', e.target.value)}
                      placeholder="e.g. ccx23, t3.medium"
                      className="font-mono"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Optional. Provisioning's preflight will fill in CPU / RAM / disk.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="srv-continent" className="flex items-center gap-1">
                      Continent
                      {geoFilled.continent && (
                        <Sparkles className="h-3 w-3 text-primary" aria-label="auto-filled" />
                      )}
                    </Label>
                    <Input
                      id="srv-continent"
                      value={form.region_continent}
                      onChange={(e) => set('region_continent', e.target.value)}
                      placeholder="Europe"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="srv-country" className="flex items-center gap-1">
                      Country
                      {geoFilled.country && (
                        <Sparkles className="h-3 w-3 text-primary" aria-label="auto-filled" />
                      )}
                    </Label>
                    <Input
                      id="srv-country"
                      value={form.region_country}
                      onChange={(e) => set('region_country', e.target.value)}
                      placeholder="DE"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="srv-city" className="flex items-center gap-1">
                      City
                      {geoFilled.city && (
                        <Sparkles className="h-3 w-3 text-primary" aria-label="auto-filled" />
                      )}
                    </Label>
                    <Input
                      id="srv-city"
                      value={form.region_city}
                      onChange={(e) => set('region_city', e.target.value)}
                      placeholder="Falkenstein"
                      className="font-mono"
                    />
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-destructive" role="alert">
                    {error}
                  </p>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={create.isPending}>
                    {create.isPending ? 'Creating…' : 'Create server'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
